import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { DashboardWidget, WidgetDataResult } from '../../../redux/wallboardApi';
import { useDashboardContext, WIDGET_COLORS } from '../pages/DashboardViewPage';
/**
 * Bar Graph Widget
 * 
 * Displays data as a bar chart.
 * Supports dynamic font sizing and themed colors.
 */
interface BarGraphWidgetProps {
  widget: DashboardWidget;
  data?: WidgetDataResult;
  colorIndex?: number;
}

export const BarGraphWidget: React.FC<BarGraphWidgetProps> = ({
  widget,
  data,
  colorIndex = 0,
}) => {
  const displayOptions = widget.config?.displayOptions || {};
  const { fontSize, onDrillDown } = useDashboardContext();
  
  // Get color theme based on index
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  // Use valueColor for text on white background, header for chart fills
  const textColor = colorTheme.valueColor || colorTheme.header;
  const chartColor = colorTheme.header;

  // Check if data is single value (no groupBy configured)
  const isSingleValue = data?.data && 
    typeof data.data === 'object' && 
    !Array.isArray(data.data) && 
    'value' in data.data && 
    !('labels' in data.data);

  // Parse data
  const { xAxisData, seriesData } = React.useMemo(() => {
    if (!data?.data) return { xAxisData: [], seriesData: [] };

    // Handle grouped data format
    if (data.data.labels && data.data.values) {
      return {
        xAxisData: data.data.labels as string[],
        seriesData: data.data.values as number[],
      };
    }

    // Handle array format
    if (Array.isArray(data.data)) {
      return {
        xAxisData: data.data.map((item: any, i: number) => 
          item.label || item.name || `Item ${i + 1}`
        ),
        seriesData: data.data.map((item: any) => 
          item.value || item.count || 0
        ),
      };
    }

    return { xAxisData: [], seriesData: [] };
  }, [data]);

  // Single value - need groupBy for bar charts
  if (isSingleValue) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 2,
        }}
      >
        <Typography 
          sx={{ 
            color: textColor, 
            fontSize: `${1.5 * fontSize}rem`,
            fontWeight: 'bold',
            mb: 1,
          }}
        >
          {(data?.data?.value ?? 0).toLocaleString()}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.75 * fontSize}rem` }}>
          Configure &quot;Group By&quot; in widget settings for bar chart
        </Typography>
      </Box>
    );
  }

  // No data
  if (xAxisData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.875 * fontSize}rem` }}>No data available</Typography>
      </Box>
    );
  }

  // Format value for tooltip/labels
  const valueFormatter = (value: number | null) => {
    if (value === null) return '';
    const unit = widget.config?.unit || '';
    if (unit === '$') {
      return `$${value.toLocaleString()}`;
    }
    return `${value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  };

  // Calculate font sizes based on fontSize multiplier
  const tickFontSize = Math.round(12 * fontSize);

  // Calculate appropriate left margin based on max value length
  const maxValue = Math.max(...seriesData, 0);
  const maxValueLength = maxValue.toLocaleString().length;
  const leftMargin = Math.max(50, 15 + maxValueLength * 8);

  // Use ResizeObserver to get actual container dimensions
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = React.useState({ width: 400, height: 300 });

  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      // Use Math.floor to avoid sub-pixel overflow
      const width = Math.floor(entry.contentRect.width);
      const height = Math.floor(entry.contentRect.height);
      if (width > 0 && height > 0) {
        setDimensions({ width, height });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Truncate long labels for better display
  const truncateLabel = (label: string, maxLen: number) =>
    label.length > maxLen ? label.slice(0, maxLen) + '…' : label;

  // Determine if labels need rotation based on available width per bar
  const availableChartWidth = dimensions.width - leftMargin - 20; // subtract left + right margins
  const widthPerBar = xAxisData.length > 0 ? availableChartWidth / xAxisData.length : availableChartWidth;
  const longestLabel = Math.max(...xAxisData.map(l => String(l).length), 0);
  const estimatedLabelWidth = longestLabel * tickFontSize * 0.6; // rough char width

  // If labels fit horizontally, keep them horizontal; otherwise rotate
  const needsRotation = estimatedLabelWidth > widthPerBar;

  // Bottom margin: horizontal labels need less space than rotated ones
  const bottomMargin = needsRotation
    ? Math.max(40, Math.min(
        Math.floor(longestLabel * tickFontSize * 0.35 + 20),
        Math.floor(dimensions.height * 0.45)
      ))
    : Math.max(25, tickFontSize + 20);

  const topMargin = 10;

  // Truncate labels if rotated and still too long
  const maxCharsForLabel = needsRotation
    ? Math.max(8, Math.floor(bottomMargin / (tickFontSize * 0.5)))
    : Math.max(3, Math.floor(widthPerBar / (tickFontSize * 0.6)));

  const truncatedXAxisData = xAxisData.map(l => truncateLabel(String(l), maxCharsForLabel));

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <BarChart
        width={dimensions.width}
        height={dimensions.height}
        xAxis={[{
          data: truncatedXAxisData,
          scaleType: 'band',
          tickLabelStyle: needsRotation
            ? {
                angle: -45,
                textAnchor: 'end',
                fontSize: tickFontSize,
              }
            : {
                fontSize: tickFontSize,
                textAnchor: 'middle',
              },
        }]}
        yAxis={[{
          tickLabelStyle: {
            fontSize: tickFontSize,
          },
        }]}
        series={[{
          data: seriesData,
          valueFormatter,
          color: chartColor,
        }]}
        onItemClick={(_event, params) => {
          const label = xAxisData[params.dataIndex];
          if (label) onDrillDown(widget.id, widget.name, String(label));
        }}
        margin={{ top: topMargin, bottom: bottomMargin, left: leftMargin, right: 20 }}
        grid={{ horizontal: true }}
        hideLegend
        skipAnimation
      />
    </Box>
  );
};

export default BarGraphWidget;
